import React from "react";
import "./TileTransition.css";
import InuaLoader, { InuaLoaderVariant } from "./InuaLoader";

export interface TileTransitionProps {
  // Controls the cover/reveal state. true = tiles sweep in and cover the
  // screen; false = tiles retract and reveal whatever is underneath.
  active: boolean;
  // Tile color — e.g. #1006C5 for the buyer flow, #ffb800 for the seller
  // flow, or an arbitrary category hex (see runSellerHomeTransition /
  // handleSellerPortalReady in App.tsx). Also used below to pick which
  // INUA loader gradient reads correctly against this tile color.
  color?: string;
  // Optional caption under the INUA mark (e.g. "Fetching your data…").
  // Left undefined for the fixed-duration buyer transitions; the seller
  // enter flow sets one once it's into its real, unpredictable-length
  // data wait, so a long hold doesn't read as stalled.
  message?: string;
}

// Maps the tile's own cover color to a loader gradient that reads well
// against it. Buyer/seller's exact brand hexes get their matching
// gradient; anything else (e.g. a seller's own category theme color,
// used mid-transition once SellerPortal reports readiness) falls back to
// the generic in-app gradient rather than guessing a variant.
function variantForTileColor(color?: string): InuaLoaderVariant {
  const normalized = (color || "").toLowerCase();
  if (normalized === "#1006c5") return "buyer";
  if (normalized === "#ffb800") return "seller";
  return "app";
}

// Ported from the provided Pug/SCSS/vanilla-JS "tiles" loader: 5 horizontal
// tiles cascade in from the left (top tile first, each one 0.2s after the
// last, 0.7s to fill), with the INUA brand loader fading in once they've
// mostly finished covering the screen. Timing/markup mirrors the original
// 1:1 — only the color is configurable and the imperative class-toggling
// is replaced with a React `active` prop.
const TileTransition: React.FC<TileTransitionProps> = ({
  active,
  color = "#1006C5",
  message,
}) => {
  return (
    <div
      className={`tile-transition ${active ? "tile-transition--active" : ""}`}
      style={{ ["--tile-transition-color" as any]: color }}
      aria-hidden={!active}
    >
      <div className="tile-transition__icon">
        <InuaLoader
          variant={variantForTileColor(color)}
          scale={0.85}
          message={message}
        />
      </div>

      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} className="tile-transition__tile" />
      ))}
    </div>
  );
};

export default TileTransition;
